import { useState } from 'react'
import { getSupabaseClient } from '../services/supabase'
import './RegistrationModal.css'

const RegistrationModal = ({ event, eventType, onClose, onSuccess }) => {
  const [email, setEmail] = useState('')
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState(null)
  const [messageType, setMessageType] = useState(null) // 'success', 'error', 'resubscribed'

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!email) return

    setLoading(true)
    setMessage(null)

    try {
      const supabase = getSupabaseClient()
      if (!supabase) {
        setMessageType('error')
        setMessage('Service unavailable. Please try again later.')
        setLoading(false)
        return
      }

      // Check if email already exists for this specific event (by title and location)
      const { data: existing } = await supabase
        .from('subscribers')
        .select('email')
        .eq('email', email)
        .eq('title', event.title)
        .eq('location', eventType)
        .maybeSingle()

      if (existing) {
        setMessageType('resubscribed')
        setMessage('You have already registered for updates about this event.')
        setLoading(false)
        return
      }

      // Insert new subscriber
      const { error } = await supabase
        .from('subscribers')
        .insert([{ 
          email, 
          location: eventType, 
          title: event.title 
        }])

      if (error) {
        console.error('Insert error:', error)
        setMessageType('error')
        setMessage('Registration failed. Please try again later.')
      } else {
        setMessageType('success')
        setMessage('Registration successful! You will receive a confirmation email shortly.')
        setTimeout(() => {
          onSuccess()
        }, 2000)
      }
    } catch (err) {
      console.error('Unexpected error:', err)
      setMessageType('error')
      setMessage('An unexpected error occurred. Please try again later.')
    } finally {
      setLoading(false)
    }
  }

  const getMessageContent = () => {
    if (!message) return null

    if (messageType === 'resubscribed') {
      return (
        <div className="success-message">
          <i className="fas fa-check-circle"></i>
          <h3>Already Registered!</h3>
          <p>You have already registered to updates for {event.title}.</p>
          <button className="close-btn" onClick={onClose}>Close</button>
        </div>
      )
    }

    if (messageType === 'success') {
      return (
        <div className="success-message">
          <i className="fas fa-check-circle"></i>
          <h3>Registration Successful!</h3>
          <p>You have successfully registered for {event.title}.</p>
          <p>You will receive a confirmation email shortly.</p>
          <button className="close-btn" onClick={onClose}>Close</button>
        </div>
      )
    }

    if (messageType === 'error') {
      return (
        <div className="error-message">
          <i className="fas fa-exclamation-circle"></i>
          <h3>Registration Failed</h3>
          <p>{message}</p>
          <button className="close-btn" onClick={onClose}>Close</button>
        </div>
      )
    }

    return null
  }

  return (
    <div className="subscription-modal" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Register for {event.title}</h3>
          <button className="close-btn" onClick={onClose}>&times;</button>
        </div>
        <div className="modal-body">
          {message ? (
            getMessageContent()
          ) : (
            <>
              <p>Stay informed about this {eventType}. Enter your email to receive updates and reminders.</p>
              <form onSubmit={handleSubmit} className="subscription-form">
                <div className="form-group">
                  <input
                    type="email"
                    id="email"
                    name="email"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    disabled={loading}
                  />
                </div>
                <div className="form-group">
                  <label className="checkbox-container">
                    <input
                      type="checkbox"
                      id="notifications"
                      name="notifications"
                      disabled
                      checked
                      readOnly
                    />
                    <span className="checkmark"></span>
                    Receive email notifications about this {eventType}
                  </label>
                </div>
                <div className="form-group">
                  <label className="checkbox-container">
                    <input
                      type="checkbox"
                      id="updates"
                      name="updates"
                      disabled
                      checked
                      readOnly
                    />
                    <span className="checkmark"></span>
                    Receive updates about future {eventType}s
                  </label>
                </div>
                <button type="submit" className="submit-btn" disabled={loading}>
                  <i className={`fas ${loading ? 'fa-spinner fa-spin' : 'fa-paper-plane'}`}></i>
                  {loading ? 'Processing...' : 'Register'}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default RegistrationModal

